// src/components/shell/popovers/ImpersonationPopover.tsx
// Dev-only sidebar entry that exposes the developer simulator — currently
// two co-equal axes: impersonation roles and the dev mobile viewport.
// Self-gates on the user's REAL role (not the impersonated overlay) so
// accidentally dropping platform_dev via impersonation cannot hide this
// control. Renamed from "Impersonation" → "Dev simulator" per dev-sync note
// 1c566b19; the two axes are independent (turning impersonation off does
// NOT clear the viewport, and vice versa).
//
// Visually distinct from the peer global items per doctrine answer A5 (amber chip
// + amber accent) until the palette pass formalizes a dev-tools color token.
import { createPortal } from "react-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useDevViewport } from "@/hooks/useDevViewport";
import { DevImpersonationSettingsSection } from "@/components/common/DevImpersonationSettingsSection";
import { DevFieldEditLogSection } from "@/components/common/DevFieldEditLogSection";
import { SidebarRailButton } from "@/components/shell/Sidebar/SidebarRailButton";
import { railPopoverPanelStyle, useRailPopover } from "./useRailPopover";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export type ImpersonationPopoverProps = {
  expanded: boolean;
  onAfterAction?: () => void;
};

export function ImpersonationPopover({ expanded, onAfterAction }: ImpersonationPopoverProps) {
  const auth = useAuth();
  const viewport = useDevViewport();
  const popover = useRailPopover();
  const canSee = auth.status === "ready" && auth.realHasRole("platform_dev", "dev");

  if (!canSee) return null;

  const { open, toggle, setOpen, triggerRef, panelRef, position } = popover;

  // "(on)" suffix lights when EITHER axis is active — both are co-equal so
  // either one is enough to consider the simulator "live".
  const anyAxisActive = auth.impersonation.active || viewport.state.active;

  const chipColor = anyAxisActive
    ? "bg-amber-600 text-white animate-pulse"
    : "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200";

  return (
    <>
      <SidebarRailButton
        ref={triggerRef}
        expanded={expanded}
        glyph="Dv"
        label={anyAxisActive ? "Dev simulator (on)" : "Dev simulator"}
        glyphChipClassName={chipColor}
        active={open}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        {...{ [FIREFLY_TARGET_ATTR]: "dev-simulator" }}
      />
      {open
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Developer simulator"
              className="rounded-2xl border border-amber-300 bg-white text-gray-900 shadow-2xl dark:border-amber-700 dark:bg-zinc-950 dark:text-gray-100"
              style={railPopoverPanelStyle(position)}
            >
              {/* Header is sticky inside the scroll container so the X stays
                  reachable on mobile no matter how far the user scrolls the
                  long impersonation + dev-field-edit content. Dev sync note
                  e3e039e2. The header carries the same bg as the panel and a
                  bottom border so it visually replaces the seam when pinned. */}
              <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-4 py-3 border-b border-amber-200/80 dark:border-amber-800/60 bg-white dark:bg-zinc-950 rounded-t-2xl">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-200">Developer simulator</h2>
                  <p className="text-[10px] leading-snug text-amber-800/80 dark:text-amber-300/80">
                    Two independent axes: <strong>impersonation</strong> + <strong>mobile viewport</strong>. Either one
                    alone is enough to "be on."
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onAfterAction?.();
                  }}
                  aria-label="Close developer simulator"
                  className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-base leading-none"
                >
                  ✕
                </button>
              </div>
              <div className="px-3 py-3">
                <DevImpersonationSettingsSection containerOpen={open} auth={auth} />
                <DevFieldEditLogSection containerOpen={open} />
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
